import { actionCreator, ActionReturnType } from "@itk/typescript-utils";
import { TAttributeFilter, TAttributeQueryFilter, TCustomFilter } from "./filter-types";

export enum Action {
  SetFormData = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:FILTER:FILTER-STORE.TS:ACTION:SETFORMDATA",
  SetFormDataAttributes = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:FILTER:FILTER-STORE.TS:ACTION:SETFORMDATAATTRIBUTES",
  SetFormDataCustomFilters = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:FILTER:FILTER-STORE.TS:ACTION:SETFORMDATACUSTOMFILTERS",
  ResetFormData = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:FILTER:FILTER-STORE.TS:ACTION:RESETFORMDATA",

  Clear = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:FILTER:FILTER-STORE.TS:ACTION:CLEAR"
}

type IFilterState = {
  formData: TAttributeQueryFilter & { customFilters: TCustomFilter[] };
};

// The date range is no longer part of this panel's form (owned by configurator.tsx's header instead),
// so formData only ever needs attributes/customFilters — no "fresh per call" default is needed anymore.
const getDefaultFormData = (): IFilterState["formData"] => ({
  attributes: [],
  customFilters: []
});

const initialState: IFilterState = {
  formData: getDefaultFormData()
};

export const filterActions = {
  setFormData: (data: IFilterState["formData"]) => actionCreator(Action.SetFormData, { data }),
  setFormDataAttributes: (attribute: TAttributeFilter) => actionCreator(Action.SetFormDataAttributes, { attribute }),
  setFormDataCustomFilters: (customFilters: IFilterState["formData"]["customFilters"]) => actionCreator(Action.SetFormDataCustomFilters, { customFilters }),
  resetFormData: () => actionCreator(Action.ResetFormData),

  clear: () => actionCreator(Action.Clear)
};

export const filterReducer = (state = initialState, action: ActionReturnType<typeof filterActions>): IFilterState => {
  switch (action.type) {
    case Action.SetFormData:
      return { ...state, formData: action.payload.data };
    case Action.SetFormDataAttributes: {
      const existing = state.formData.attributes || [];
      const index = existing.findIndex(x => x.id === action.payload.attribute.id);
      // Replace the entry instead of assigning into it. The old in-place `values` write kept the
      // entry's object identity, so a memoized filter row could not tell that its own values changed.
      const newAttributes = index >= 0 ? existing.map((x, i) => (i === index ? { ...x, values: action.payload.attribute.values } : x)) : [...existing, action.payload.attribute];
      return {
        ...state,
        formData: {
          ...state.formData,
          attributes: newAttributes
        }
      };
    }
    case Action.SetFormDataCustomFilters:
      return { ...state, formData: { ...state.formData, customFilters: action.payload.customFilters } };

    case Action.ResetFormData:
      return { ...state, formData: getDefaultFormData() };

    case Action.Clear:
      return { ...initialState, formData: getDefaultFormData() };
    default:
      return state;
  }
};
